//
/* GS://PROTOCOLO
 * Arquivo: src/pages/admin/PushCenterPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.1 – BRANDING: Correção do Nome da Marca
 * Impacto da Versão: [src/pages/admin/PushCenterPage.js]
 * Data: 07/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [BRANDING]: Alterado de "Golden Sisters" para "GoldenSisters" no preview do celular.
 */

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { Bell, Send, Users, AlertCircle, CheckCircle, Smartphone } from 'lucide-react';

const PushCenterPage = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('all'); // 'all', 'emails'
  const [targetList, setTargetList] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // Função de Envio
  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      setStatus({ type: 'error', message: 'Título e Mensagem são obrigatórios.' });
      return;
    }

    setIsLoading(true);
    setStatus({ type: 'info', message: 'Enviando...' });

    try {
      const functions = getFunctions(getApp(), 'southamerica-east1');
      const sendPush = httpsCallable(functions, 'sendAdminPush');

      const payload = {
        title,
        body,
        targetType,
        targetList: targetType === 'emails' ? targetList.split('\n').filter(e => e.trim()) : [],
      };

      const result = await sendPush(payload);
      
      if (result.data.success) {
        setStatus({ type: 'success', message: `Sucesso! Enviado para ${result.data.count || 'vários'} dispositivos.` });
      } else {
        setStatus({ type: 'error', message: 'O servidor retornou um erro.' });
      }
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', message: `Erro no envio: ${error.message}` });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto bg-brand-black text-gray-100">
      <div className="max-w-6xl mx-auto">
        
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Bell className="text-brand-gold h-8 w-8" />
            Push Center
          </h1>
          <p className="text-gray-400 mt-2">Envie notificações em tempo real para os aplicativos dos clientes.</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* COLUNA DA ESQUERDA: FORMULÁRIO */}
          <div className="bg-gray-900 p-6 rounded-lg border border-gray-800 shadow-lg">
            <form onSubmit={handleSend} className="space-y-6">
              
              {/* Seleção de Público */}
              <div>
                <label className="block text-sm font-medium text-brand-gold mb-2 flex items-center gap-2">
                  <Users size={16} /> Destinatários
                </label>
                <div className="flex gap-4 mb-3">
                  <button
                    type="button"
                    onClick={() => setTargetType('all')}
                    className={`flex-1 py-2 px-4 rounded border ${targetType === 'all' ? 'bg-brand-gold text-brand-black border-brand-gold font-bold' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                  >
                    Todos os Usuários
                  </button>
                  <button
                    type="button"
                    onClick={() => setTargetType('emails')}
                    className={`flex-1 py-2 px-4 rounded border ${targetType === 'emails' ? 'bg-brand-gold text-brand-black border-brand-gold font-bold' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                  >
                    Lista de Emails
                  </button>
                </div>
                
                {targetType === 'emails' && (
                  <textarea
                    placeholder="Cole os e-mails aqui (um por linha)..."
                    value={targetList}
                    onChange={(e) => setTargetList(e.target.value)}
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white h-32 focus:border-brand-gold outline-none font-mono text-sm"
                  />
                )}
              </div>

              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Título da Notificação</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Novidade Imperdível! 💎"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white focus:border-brand-gold outline-none text-lg font-medium"
                  maxLength={50}
                />
                <div className="text-right text-xs text-gray-500 mt-1">{title.length}/50</div>
              </div>

              {/* Mensagem (Corpo) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Mensagem</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Digite sua mensagem aqui. Seja criativo e direto..."
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white h-40 focus:border-brand-gold outline-none text-base resize-none"
                  maxLength={150}
                />
                <div className="text-right text-xs text-gray-500 mt-1">{body.length}/150</div>
              </div>

              {/* Botão de Envio */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-brand-gold hover:bg-yellow-500 text-brand-black font-bold rounded-lg text-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  'Enviando...'
                ) : (
                  <>
                    <Send size={20} /> Enviar Notificação
                  </>
                )}
              </button>

              {/* Status Message */}
              {status.message && (
                <div className={`p-4 rounded flex items-center gap-3 ${status.type === 'error' ? 'bg-red-900/50 text-red-200' : 'bg-green-900/50 text-green-200'}`}>
                  {status.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                  {status.message}
                </div>
              )}

            </form>
          </div>

          {/* COLUNA DA DIREITA: PREVIEW */}
          <div className="flex flex-col items-center justify-start pt-4">
            <h3 className="text-gray-400 mb-4 flex items-center gap-2">
              <Smartphone size={18} /> Pré-visualização no Celular
            </h3>
            
            {/* Mockup de Celular */}
            <div className="relative w-[300px] h-[600px] bg-gray-800 rounded-[3rem] border-8 border-gray-700 shadow-2xl overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-700 rounded-b-xl z-10"></div>
              
              {/* Tela */}
              <div className="w-full h-full bg-gray-100 text-gray-900 pt-12 px-4 relative">
                {/* Wallpaper simulado */}
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

                {/* A Notificação */}
                <div className="relative bg-white/90 backdrop-blur-sm p-3 rounded-2xl shadow-lg border border-gray-200 animate-fade-in-down mt-2">
                  <div className="flex items-start gap-3">
                    <div className="bg-brand-gold/20 p-2 rounded-lg">
                      <img src="/logo1.png" alt="Icon" className="w-5 h-5 object-contain" onError={(e) => e.target.style.display='none'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        {/* [GS-CORRECAO: NOME DA MARCA AJUSTADO] */}
                        <span className="text-xs font-bold text-gray-700">GoldenSisters</span>
                        <span className="text-[10px] text-gray-400">Agora</span>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 leading-tight mb-1">
                        {title || 'Título da Notificação'}
                      </h4>
                      <p className="text-xs text-gray-600 leading-snug break-words">
                        {body || 'O conteúdo da sua mensagem aparecerá aqui exatamente como o cliente verá.'}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center max-w-xs">
              * A aparência real pode variar dependendo do dispositivo (Android/iOS) e configurações do usuário.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PushCenterPage;